'use client'

import React from 'react'
import { useRouter } from 'next/navigation'
import { Loader2 } from 'lucide-react'
import Wallet from '@/utils/wallet'
import event from '@/utils/event'
import isRune from '@/utils/is-rune'
import { fetchInscriptionListing } from '@/utils/hooks/useInscriptionListing'
import type { SimilarItem } from '@/utils/similar-listings'
import styles from './similar-listings.module.css'

/**
 * Buy straight from a similar-listings card. The card's payload carries only
 * the price, so the click fetches the live listing (seller, outpoint,
 * protection markers) and opens the same purchase modal the item page uses.
 * A listing that sold meanwhile drops out of the grid instead of opening a
 * dead modal.
 */
export default function SimilarBuyNow({ item, onGone }: { item: SimilarItem; onGone: () => void }) {
	const router = useRouter()
	const [busy, setBusy] = React.useState(false)

	const buy = async (evt: React.MouseEvent) => {
		evt.preventDefault()
		evt.stopPropagation()
		if (busy) return
		if (!Wallet.isSet()) {
			event.emit('snackbar', { title: 'Please create a wallet', variant: 'success' })
			router.push('/create-wallet')
			return
		}
		setBusy(true)
		try {
			const listing = await fetchInscriptionListing(item.id, true)
			if (!listing) {
				event.emit('snackbar', { title: 'That one just sold', variant: 'success' })
				onGone()
				return
			}
			const inscription = { ...item, escrow: listing }
			const price = Number(listing.satoshi_price)
			event.emit('open-purchase-modal', {
				inscription,
				isBRC20: false,
				purchaseData: {
					autoSign: false,
					marketFee: (price * 2.7) / 100,
					subtotalPrice: price,
					rune: isRune(item.collection?.slug as any),
					slug: item.collection?.slug
				}
			})
		} catch (e: any) {
			event.emit('snackbar', {
				title: e?.message || 'Could not check this listing. Please try again.',
				variant: 'destructive'
			})
		} finally {
			setBusy(false)
		}
	}

	return (
		<button type="button" onClick={buy} disabled={busy} className={styles.buy} aria-busy={busy}>
			{busy ? <Loader2 size={13} className="animate-spin" aria-hidden /> : null}
			<span>{busy ? 'Checking…' : 'Buy now'}</span>
		</button>
	)
}
